import React from 'react';
import { Plus, Minus, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { products } from '../data/products';
import { useCart } from '../context/CartContext';

export const ShopSection = () => {
  const { items, addItem, updateQty } = useCart();

  const getQty = (id) => items.find((i) => i.id === id)?.qty || 0;

  return (
    <section id="shop" className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="shop-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block bg-[#ff8c19]/10 text-[#ff8c19] text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            Shop Online · Home Delivery
          </span>
          <h2 className="text-4xl font-bold text-[#1e1919] mb-4">Shop Our Products</h2>
          <p className="text-lg text-[#736c64] max-w-2xl mx-auto">
            100% natural, home-made treats & supplements. No preservatives, no additives. Add to cart and order in minutes.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product) => {
            const qty = getQty(product.id);
            return (
              <div
                key={product.id}
                className="group bg-[#f7f5f2] rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300 flex flex-col"
                data-testid={`product-card-${product.id}`}
              >
                <div className="relative aspect-square overflow-hidden bg-white">
                  <img
                    src={product.image}
                    alt={product.name}
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                  />
                  <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-[#61525a] text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm">
                    {product.weight}
                  </span>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="text-base font-semibold text-[#1e1919] leading-snug mb-1 min-h-[44px]">
                    {product.name}
                  </h3>
                  <div className="text-xl font-bold text-[#61525a] mb-4">₹{product.price}</div>

                  <div className="mt-auto">
                    {qty === 0 ? (
                      <Button
                        onClick={() => addItem(product)}
                        className="w-full bg-[#ff8c19] hover:bg-[#e67d16] text-white rounded-full py-5 font-semibold"
                        data-testid={`add-to-cart-${product.id}`}
                      >
                        <ShoppingCart className="mr-2" size={18} /> Add to Cart
                      </Button>
                    ) : (
                      <div className="flex items-center justify-between bg-white rounded-full p-1 border border-[#61525a]/20">
                        <button
                          onClick={() => updateQty(product.id, qty - 1)}
                          className="h-9 w-9 flex items-center justify-center rounded-full bg-[#f7f5f2] hover:bg-[#e8e6e3] text-[#1e1919] transition-colors"
                          data-testid={`decrease-${product.id}`}
                          aria-label="Decrease quantity"
                        >
                          <Minus size={16} />
                        </button>
                        <span className="font-semibold text-[#1e1919]" data-testid={`qty-${product.id}`}>{qty}</span>
                        <button
                          onClick={() => updateQty(product.id, qty + 1)}
                          className="h-9 w-9 flex items-center justify-center rounded-full bg-[#ff8c19] hover:bg-[#e67d16] text-white transition-colors"
                          data-testid={`increase-${product.id}`}
                          aria-label="Increase quantity"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
